<template>
  <div class="flex flex-col justify-center items-center">
    <div
      v-if="!showYoutubeTranscriptor"
      class="flex flex-col lg:flex-row lg:space-x-4 justify-center items-center h-auto w-full"
    >
      <div
        class="flex flex-col items-center lg:items-start justify-center w-[95%] lg:w-[50%] xl:w-[45%] 2xl:w-[40%]"
      >
        <p
          class="text-lg md:text-2xl lg:text-4xl pt-1 md:pt-6 text-center lg:text-start text-[#061B44] font-bold"
        >
          {{ $t("Welcome_to_Subtitles_Technology") }}
        </p>
        <p
          class="text-sm md:text-base lg:text-lg w-full pt-6 text-center lg:text-start text-black font-semibold"
        >
          {{ $t("Your_Gateway_to_Multilingual_Video_and") }}
        </p>
        <div
          class="flex flex-row flex-wrap justify-center gap-3 sm:gap-4 items-center pt-6"
        >
          <div
            @click="navToRoute('youtube-transcription')"
            class="flex flex-row gap-3 sm:gap-4 items-center cursor-pointer transition-all duration-200 hover:scale-105 hover:opacity-80"
            role="button"
            tabindex="0"
            @keydown.enter="navToRoute('youtube-transcription')"
            aria-label="Navigate to YouTube transcription service"
          >
            <div
              class="flex shrink-0 items-center justify-center overflow-hidden rounded-full w-10 h-10 md:w-12 md:h-12 lg:w-14 lg:h-14"
            >
              <img
                src="../assets/images/home/01.png"
                alt="YouTube transcription service icon"
                class="h-full w-full object-contain"
                loading="lazy"
                decoding="async"
                width="56"
                height="56"
              />
            </div>
            <p
              class="text-xs md:text-base font-medium md:font-semibold text-black"
              v-html="formattedYoutubeText"
            ></p>
          </div>
          <div
            @click="navToRoute('video-transcription')"
            class="flex flex-row gap-3 sm:gap-4 items-center cursor-pointer transition-all duration-200 hover:scale-105 hover:opacity-80"
            role="button"
            tabindex="0"
            @keydown.enter="navToRoute('video-transcription')"
            aria-label="Navigate to video transcription service"
          >
            <div
              class="flex shrink-0 items-center justify-center overflow-hidden rounded-full w-10 h-10 md:w-12 md:h-12 lg:w-14 lg:h-14"
            >
              <img
                src="../assets/images/home/02.png"
                alt="Video transcription service icon"
                class="h-full w-full object-contain"
                loading="lazy"
                decoding="async"
                width="56"
                height="56"
              />
            </div>
            <p
              class="text-xs md:text-base font-medium md:font-semibold text-black"
              v-html="formattedVideoText"
            ></p>
          </div>
          <div
            @click="navToRoute('audio-transcription')"
            class="flex flex-row gap-3 sm:gap-4 items-center cursor-pointer transition-all duration-200 hover:scale-105 hover:opacity-80"
            role="button"
            tabindex="0"
            @keydown.enter="navToRoute('audio-transcription')"
            aria-label="Navigate to audio transcription service"
          >
            <div
              class="flex shrink-0 items-center justify-center overflow-hidden rounded-full w-10 h-10 md:w-12 md:h-12 lg:w-14 lg:h-14"
            >
              <img
                src="../assets/images/home/03.png"
                alt="Audio transcription service icon"
                class="h-full w-full object-contain"
                loading="lazy"
                decoding="async"
                width="56"
                height="56"
              />
            </div>
            <p class="text-xs md:text-base font-medium md:font-semibold text-black">
              {{ $t("Audio") }}<br />{{ $t("to_text") }}
            </p>
          </div>
        </div>
        <p
          class="text-xs md:text-base lg:text-lg w-full pt-6 pb-4 text-center lg:text-start text-black font-semibold"
        >
          {{ $t("Free_YouTube_Video_Transcription") }}
        </p>
      </div>

      <div
        id="lottie-container"
        class="w-[98%] lg:w-[40%] xl:w-[40%] 2xl:w-[40%] relative aspect-[6/5] mb-10 md:mb-0"
        style="min-height: 400px;"
      >
        <img
          v-if="!isAnimationLoaded"
          src="../assets/images/home/04.png"
          alt="Subtitles Tech animation placeholder"
          loading="eager"
          fetchpriority="high"
          decoding="async"
          width="600"
          height="500"
          class="w-full h-full object-contain"
        />
        <canvas
          id="lottie-canvas"
          class="w-full"
          :class="{
            'h-[80%]': isAnimationLoaded,
            'h-0': !isAnimationLoaded,
          }"
          :style="{ opacity: isAnimationLoaded ? 1 : 0 }"
        ></canvas>
        <div
          v-if="isAnimationLoaded"
          class="flex flex-col items-center justify-center text-center"
        >
          <div
            v-for="(text, index) in animatedText"
            :key="index"
            class="animated-text"
            :class="{
              'text-active': currentTextIndex === index,
              'text-inactive': currentTextIndex !== index,
            }"
          >
            <p
              class="text-xl lg:text-2xl font-bold text-subBlue drop-shadow-md"
            >
              {{ t(text.title) }}
            </p>
            <p
              v-if="text.subtitle"
              class="text-xl lg:text-2xl font-bold text-subBlue drop-shadow-md"
            >
              {{ t(text.subtitle) }}
            </p>
          </div>
        </div>
        <button
          @click="
            authStore.token
              ? navToRoute('pricing')
              : navToRoute('free-youtube-transcription')
          "
          class="bg-subOrange text-sm md:text-base font-semibold px-6 md:px-12 rounded-lg text-white h-8 md:h-10 absolute left-1/2 transform -translate-x-1/2 mt-10 text-nowrap transition-all duration-200 hover:scale-105 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-subOrange focus:ring-offset-2"
          aria-label="Get started for free or explore subscription plans"
        >
          {{
            authStore.token
              ? $t("Explore_Subscription_Plans")
              : $t("Get_started_For_free")
          }}
        </button>
      </div>
    </div>
    <!-- services selection -->
    <div class="hidden">
      <div
        class="container bg-subWhite w-[95%] lg:w-[70%] rounded-lg h-14 mt-14 flex flex-row"
      >
        <button
          @click="selectService(0)"
          :class="{
            'bg-subBlue text-white': selectedServiceIndex == 0,
            'bg-subWhite': selectedServiceIndex != 0,
          }"
          class="text-black font-bold text-sm lg:text-lg w-full rounded-lg h-14"
        >
          <p>{{ $t("youtube-video-transcription") }}</p>
        </button>
        <button
          @click="selectService(1)"
          :class="{
            'bg-subBlue text-white': selectedServiceIndex == 1,
            'bg-subWhite': selectedServiceIndex != 1,
          }"
          class="text-black font-bold text-sm lg:text-lg w-full rounded-lg h-14"
        >
          <p>{{ $t("video-transcription") }}</p>
        </button>
        <button
          @click="selectService(2)"
          :class="{
            'bg-subBlue text-white': selectedServiceIndex == 2,
            'bg-subWhite': selectedServiceIndex != 2,
          }"
          class="text-black font-bold text-sm lg:text-lg w-full rounded-lg h-14"
        >
          <p>{{ $t("audio-transcription") }}</p>
        </button>
      </div>

      <div
        v-if="selectedServiceIndex == 0"
        class="container flex flex-col items-center mt-12"
      >
        <YoutubeComponent></YoutubeComponent>
      </div>
      <div
        v-else-if="selectedServiceIndex == 1"
        class="container flex flex-col items-center mt-12"
      >
        <VideoComponent></VideoComponent>
      </div>
      <div v-else class="container flex flex-col items-center mt-12">
        <AudioComponent></AudioComponent>
      </div>
      <div class="pt-28"></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch, defineAsyncComponent } from "vue";
import { useI18n } from "vue-i18n";
import { useUserActivities } from "../store/userActivities";
import { useAuthStore } from "../store/auth";
// Lazy load heavy components - only load when needed
const AudioComponent = defineAsyncComponent(() => import("../components/audio_component.vue"));
const VideoComponent = defineAsyncComponent(() => import("../components/video_component.vue"));
const YoutubeComponent = defineAsyncComponent(() => import("../components/youtube_component.vue"));
import { useRouter } from "vue-router";
import { useHead } from "@vueuse/head";
import { PRODUCTIONFRONTEND } from "../utils/appConst";

// Animation duration constants (in milliseconds)
const ANIMATION_DURATIONS = {
  WE_TRANSCRIBE: 8450,
  WE_DO_CAPTIONS: 6210,
  WE_TRANSLATE: 6710,
  WE_SUBTITLE: 5170,
  ON_ALL_DEVICES: 6500,
  LANGUAGES_140: 5000,
  AUDIO_TO_TEXT: 5500,
  VIDEO_SUBTITLES: 5570,
  YOUTUBE_SUBTITLES: 6500,
} as const;

// Timing constants (in milliseconds)
const TIMING = {
  TEXT_ANIMATION_DELAY: 500, // Delay between text transitions
  LOTTIE_FALLBACK_TIMEOUT: 500, // Fallback timeout for Lottie animation loading
  ANIMATION_CHECK_INTERVAL: 100, // How often to check if text should transition (state machine tick)
} as const;

// Animation state machine
type AnimationState = {
  currentIndex: number;
  startTime: number;
  isRunning: boolean;
};

const animationState = ref<AnimationState>({
  currentIndex: 0,
  startTime: 0,
  isRunning: false,
});

// IntersectionObserver configuration
const INTERSECTION_OBSERVER_CONFIG = {
  THRESHOLD: 0.01, // Trigger when 1% of element is visible
  ROOT_MARGIN: "200px", // Start loading 200px before element is visible
} as const;

// Logger utility - only logs in development mode
const logger = {
  log: (...args: any[]) => {
    if (import.meta.env.DEV) {
      console.log(...args);
    }
  },
  error: (...args: any[]) => {
    if (import.meta.env.DEV) {
      console.error(...args);
    }
  },
};

const activitesStore = useUserActivities();
const authStore = useAuthStore();
const router = useRouter();
const { t, locale } = useI18n();
const showYoutubeTranscriptor = computed(
  () => activitesStore.freeYoutubeService
);
let lottieInstance: any = null;
/** Prevents double DotLottie init (IntersectionObserver + timeout both calling load). */
let lottieLoadStarted = false;
const isAnimationLoaded = ref(false);
const currentTextIndex = ref(0);
let textInterval: ReturnType<typeof setInterval> | null = null;
let lottieTimeoutId: ReturnType<typeof setTimeout> | null = null;
let intersectionObserver: IntersectionObserver | null = null;
const selectedServiceIndex = ref(1);

const animatedText = computed(() => [
  {
    title: t("we-transcribe"),
    subtitle: t("for-you"),
    duration: ANIMATION_DURATIONS.WE_TRANSCRIBE,
  },
  {
    title: t("we-do-captions"),
    subtitle: t("for-you"),
    duration: ANIMATION_DURATIONS.WE_DO_CAPTIONS,
  },
  {
    title: t("we-translate"),
    subtitle: t("for-you"),
    duration: ANIMATION_DURATIONS.WE_TRANSLATE,
  },
  {
    title: t("we-subtitle"),
    subtitle: t("for-you"),
    duration: ANIMATION_DURATIONS.WE_SUBTITLE,
  },
  {
    title: t("on-all-devices"),
    subtitle: "",
    duration: ANIMATION_DURATIONS.ON_ALL_DEVICES,
  },
  {
    title: t("140-languages"),
    subtitle: "",
    duration: ANIMATION_DURATIONS.LANGUAGES_140,
  },
  {
    title: t("audio-to-text"),
    subtitle: "",
    duration: ANIMATION_DURATIONS.AUDIO_TO_TEXT,
  },
  {
    title: t("video-subtitles"),
    subtitle: "",
    duration: ANIMATION_DURATIONS.VIDEO_SUBTITLES,
  },
  {
    title: t("youtube-subtitles"),
    subtitle: "",
    duration: ANIMATION_DURATIONS.YOUTUBE_SUBTITLES,
  },
]);

const formattedYoutubeText = computed(() => {
  return locale.value === "ar"
    ? `${t("subtitles")}<br />${t("Youtube")}`
    : `${t("Youtube")}<br />${t("subtitles")}`;
});

const formattedVideoText = computed(() => {
  return locale.value === "ar"
    ? `${t("subtitles")}<br />${t("Video")}`
    : `${t("Video")}<br />${t("subtitles")}`;
});

/**
 * State machine for text animation
 * Uses a single fixed-interval timer that checks if it's time to transition
 * Much cleaner and easier to debug than nested intervals
 */
const transitionToNextText = () => {
  // Safety check: ensure we have text items
  if (animatedText.value.length === 0) return;
  
  const nextIndex = (animationState.value.currentIndex + 1) % animatedText.value.length;
  animationState.value.currentIndex = nextIndex;
  animationState.value.startTime = Date.now();
  currentTextIndex.value = nextIndex;
};

const checkAndTransition = () => {
  if (!animationState.value.isRunning) return;

  // Safety check: ensure current index is valid
  if (animationState.value.currentIndex >= animatedText.value.length) {
    animationState.value.currentIndex = 0;
    currentTextIndex.value = 0;
    animationState.value.startTime = Date.now();
    return;
  }

  const currentDuration = animatedText.value[animationState.value.currentIndex].duration;
  const elapsed = Date.now() - animationState.value.startTime;
  const totalDuration = currentDuration + TIMING.TEXT_ANIMATION_DELAY;

  if (elapsed >= totalDuration) {
    transitionToNextText();
  }
};

const startTextAnimation = () => {
  // Safety check: ensure we have text items to animate
  if (animatedText.value.length === 0) {
    logger.log("No text items to animate");
    return;
  }

  // Stop any existing animation
  stopTextAnimation();

  // Initialize state machine
  animationState.value = {
    currentIndex: 0,
    startTime: Date.now(),
    isRunning: true,
  };
  currentTextIndex.value = 0;

  // Start the state machine ticker (single fixed interval)
  textInterval = setInterval(checkAndTransition, TIMING.ANIMATION_CHECK_INTERVAL);
};

const stopTextAnimation = () => {
  if (textInterval) {
    clearInterval(textInterval);
    textInterval = null;
  }
  animationState.value.isRunning = false;
};

const loadLottieAnimation = async () => {
  try {
    if (lottieLoadStarted || lottieInstance) {
      return;
    }
    // Check if user prefers reduced motion
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) {
      // Skip animation for users who prefer reduced motion
      isAnimationLoaded.value = false;
      return;
    }

    const canvasEl = document.getElementById("lottie-canvas") as HTMLCanvasElement | null;
    if (!canvasEl) {
      logger.error("lottie-canvas element not found");
      return;
    }

    lottieLoadStarted = true;

    // Dynamically import Lottie library only when needed
    const { DotLottie } = await import("@lottiefiles/dotlottie-web");
    const { default: lottieAnimating } = await import(
      "../assets/animation/subtitles.lottie"
    );
    lottieInstance = new DotLottie({
      canvas: canvasEl,
      src: lottieAnimating,
      loop: true,
      autoplay: true,
      useFrameInterpolation: false, // Disable for better performance
    });
    lottieInstance.addEventListener("ready", () => {
      // Let Lottie paint at least one frame before fading poster → avoids white flash
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          isAnimationLoaded.value = true;
          startTextAnimation();
        });
      });
    });
  } catch (error) {
    logger.error("Failed to load Lottie animation:", error);
    lottieLoadStarted = false;
    isAnimationLoaded.value = false;
  }
};

onMounted(() => {
  const lottieContainer = document.getElementById("lottie-container");
  if (lottieContainer) {
    // Use IntersectionObserver with rootMargin for earlier loading
    intersectionObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadLottieAnimation();
          if (intersectionObserver) {
            intersectionObserver.disconnect();
            intersectionObserver = null;
          }
        }
      },
      { 
        threshold: INTERSECTION_OBSERVER_CONFIG.THRESHOLD,
        rootMargin: INTERSECTION_OBSERVER_CONFIG.ROOT_MARGIN
      }
    );
    intersectionObserver.observe(lottieContainer);
    // Reduced timeout for faster fallback - track for cleanup
    lottieTimeoutId = setTimeout(() => {
      if (!isAnimationLoaded.value) {
        logger.log("Fallback: Loading Lottie animation after timeout");
        loadLottieAnimation();
        if (intersectionObserver) {
          intersectionObserver.disconnect();
          intersectionObserver = null;
        }
      }
      lottieTimeoutId = null;
    }, TIMING.LOTTIE_FALLBACK_TIMEOUT);
  } else {
    logger.error("lottie-container element not found");
  }
});

onUnmounted(() => {
  // Cleanup all timers and observers
  stopTextAnimation();
  if (lottieTimeoutId) {
    clearTimeout(lottieTimeoutId);
    lottieTimeoutId = null;
  }
  if (intersectionObserver) {
    intersectionObserver.disconnect();
    intersectionObserver = null;
  }
  if (lottieInstance) {
    lottieInstance.destroy();
    lottieInstance = null;
  }
  lottieLoadStarted = false;
});

watch(locale, (newLocale) => {
  if (lottieInstance) {
    logger.log("Locale changed, update DotLottie text if applicable");
  }
});

const selectService = (index: number) => {
  if (selectedServiceIndex.value == index) return;
  selectedServiceIndex.value = index;
};

const navToRoute = (name: string) => {
  router.push(`/${name}`);
};

useHead({
  title: "Subtitles Tech - AI-Powered Transcription & Translation",
  meta: [
    {
      name: "description",
      content:
        "Subtitles Tech offers AI-powered audio to text transcription, video to text transcription, automatic subtitles generation, and translation into 140 languages. Convert MP4 to text, transcribe YouTube videos, and more.",
    },
    {
      name: "keywords",
      content:
        "Audio to text transcription, Video to text transcription, Convert video to text, Convert audio to text, Automatic subtitles generator, Online transcription tool, AI-powered transcription, Speech-to-text converter, Transcribe YouTube video to text, Download YouTube subtitles, YouTube video transcription online, Extract text from YouTube video, Convert MP4 to text, Transcribe a local audio, Transcribe a local video, Speech-to-text for PC, Translate video into 140 languages, Automatic video translation, Audio translation to text, Convert speech to multiple languages, AI subtitle translator, Online subtitle translation tool, How to extract text from a video, Best automatic transcription tool, How to transcribe an audio file quickly, Create subtitles for YouTube easily, Captioning service, Closed captioning for YouTube, Multilingual subtitles, Pay-as-you-go transcription, Voice-to-text converter, Transcription in 140 languages, AI-based captioning, Multilingual accessibility services, Translate captions",
    },
    {
      property: "og:title",
      content: "Subtitles Tech - AI-Powered Transcription & Translation",
    },
    {
      property: "og:description",
      content:
        "Subtitles Tech offers AI-powered audio to text transcription, video to text transcription, automatic subtitles generation, and translation into 140 languages. Convert MP4 to text, transcribe YouTube videos, and more.",
    },
    {
      property: "og:image",
      content: `${PRODUCTIONFRONTEND}assets/Icon-D2oZLO0r.png`,
    },
  ],
  link: [
    {
      rel: "preload",
      as: "image",
      href: "/src/assets/images/home/04.png",
      fetchpriority: "high",
    },
  ],
});
</script>

<style scoped>
.animated-text {
  position: absolute;
  opacity: 0;
  transition: opacity 0.5s ease;
}

.text-active {
  opacity: 1;
  animation: appear 0.5s ease forwards;
}

.text-inactive {
  opacity: 0;
  animation: disappear 0.5s ease forwards;
}

@keyframes appear {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes disappear {
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-20px);
  }
}
</style>
